<template>
    <div :class="$style.gallery">
        <div
            :class="$style.stage"
            role="group"
            :aria-label="$tr('steamGames.galleryLabel', { name: props.name })"
            tabindex="0"
        >
            <img
                v-for="(screenshot, index) in props.screenshots"
                :key="screenshot.full"
                :class="[$style.slide, index === activeIndex && $style.slide_active]"
                :src="screenshot.full"
                :alt="$tr('steamGames.screenshotAlt', { name: props.name, index: index + 1 })"
                width="1440"
                height="810"
                :loading="isLcpCandidate(index) ? 'eager' : 'lazy'"
                :fetchpriority="isLcpCandidate(index) ? 'high' : 'auto'"
                decoding="async"
            />
        </div>
        <div :class="$style.thumbs">
            <button
                v-for="(screenshot, index) in props.screenshots"
                :key="screenshot.thumb"
                type="button"
                :class="[$style.thumb, index === activeIndex && $style.thumb_active]"
                :aria-label="$tr('steamGames.showScreenshot', { index: index + 1 })"
                :aria-current="index === activeIndex"
                data-interface-target="steam-screenshot"
                @pointerenter="activeIndex = index"
                @focus="activeIndex = index"
                @click="activeIndex = index"
            >
                <img
                    :class="$style.thumb_image"
                    :src="screenshot.thumb"
                    alt=""
                    width="320"
                    height="180"
                    loading="lazy"
                    decoding="async"
                />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import type { SteamScreenshot } from '../games'

const props = defineProps<{
    name: string
    screenshots: SteamScreenshot[]
    priority?: boolean
}>()

const activeIndex = ref(0)

function isLcpCandidate(index: number) {
    return Boolean(props.priority) && index === 0
}
</script>

<style module>
.gallery {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.stage {
    display: flex;
    gap: 4px;
    padding-inline: var(--card-gutter);
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-padding-inline: var(--card-gutter);
    scrollbar-width: none;
}

.stage::-webkit-scrollbar {
    display: none;
}

.slide {
    flex: 0 0 100%;
    min-width: 0;
    width: 100%;
    height: auto;
    min-height: 192px;
    aspect-ratio: var(--thumbnail-aspect-ratio);
    object-fit: cover;
    border-radius: var(--radius-12);
    background: var(--carbon-400);
    scroll-snap-align: start;
}

.thumbs {
    display: none;
}

@media (--desktop-small-plus) {
    .stage {
        display: block;
        flex: 1 1 auto;
        min-height: 0;
        padding-inline: 0;
        overflow: visible;
    }

    .slide {
        display: none;
        border-radius: var(--radius-16);
    }

    .slide_active {
        display: block;
        height: 100%;
    }

    .thumbs {
        display: flex;
        flex-shrink: 0;
        gap: 4px;
    }

    .thumb {
        position: relative;
        flex: 1 0 0;
        min-width: 0;
        padding: 0;
        border-radius: 6px;
        background: none;
        overflow: hidden;
        cursor: pointer;
    }

    .thumb::after {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.32);
        transition: opacity 0.2s;
    }

    .thumb_active::after {
        opacity: 0;
    }

    .thumb:focus-visible {
        outline: 2px solid var(--electric-lime);
        outline-offset: 2px;
    }

    .thumb_image {
        display: block;
        width: 100%;
        height: auto;
        aspect-ratio: var(--thumbnail-aspect-ratio);
        object-fit: cover;
        background: var(--carbon-400);
    }
}

@media (--desktop-small-plus) and (hover: hover) {
    .thumb:not(.thumb_active):hover::after {
        opacity: 0.5;
    }
}
</style>
